import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, LayoutTemplate, Star, Eye, TrendingUp,
  ArrowRight, PlusCircle, Target, ChevronRight,
} from 'lucide-react';
import CreatorLayout from './CreatorLayout';
import VerifiedBadge from '../../components/shared/VerifiedBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const USER_KEY = 'ivisora_user';

const StatCard = ({ icon: Icon, label, value, sub, color = 'violet' }) => {
  const colors = {
    violet: 'bg-violet-50 text-violet-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    sky: 'bg-sky-50 text-sky-600',
  };
  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 mt-0.5">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
};

const GoalWidget = ({ profile, earnedCents = 0, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [goalInput, setGoalInput] = useState('');
  const [saving, setSaving] = useState(false);

  const goalCents = profile?.creator_profile?.goals?.monthly_revenue_goal_cents || 0;
  const pct = goalCents > 0 ? Math.min(100, Math.round((earnedCents / goalCents) * 100)) : 0;

  const handleSave = async () => {
    const cents = Math.round(parseFloat(goalInput) * 100);
    if (isNaN(cents) || cents < 0) return;
    setSaving(true);
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      await fetch(`${API_URL}/api/creator/goals`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ monthly_revenue_goal_cents: cents }),
      });
      onSave(cents);
      setEditing(false);
    } catch {}
    setSaving(false);
  };

  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-fuchsia-50 flex items-center justify-center">
            <Target className="w-5 h-5 text-fuchsia-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">Monthly Revenue Goal</p>
            <p className="text-xs text-gray-400">
              {goalCents > 0
                ? `$${(earnedCents / 100).toFixed(2)} of $${(goalCents / 100).toFixed(0)}`
                : 'No goal set'}
            </p>
          </div>
        </div>
        <button
          onClick={() => { setGoalInput((goalCents / 100).toFixed(0)); setEditing(true); }}
          className="text-xs text-violet-600 font-semibold hover:text-violet-700"
        >
          {goalCents > 0 ? 'Edit' : 'Set goal'}
        </button>
      </div>

      {editing ? (
        <div className="flex gap-2 mt-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
            <input
              type="number"
              min="0"
              step="10"
              value={goalInput}
              onChange={e => setGoalInput(e.target.value)}
              className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              autoFocus
            />
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50"
          >
            Save
          </button>
          <button
            onClick={() => setEditing(false)}
            className="px-3 py-2 rounded-lg text-gray-400 hover:text-gray-600 text-sm"
          >
            ✕
          </button>
        </div>
      ) : goalCents > 0 ? (
        <>
          <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
            <div
              className="bg-gradient-to-r from-violet-500 to-fuchsia-500 h-2 rounded-full transition-all duration-700"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-1.5 text-right">{pct}% of goal</p>
        </>
      ) : null}
    </div>
  );
};

const CreatorDashboard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentTemplates, setRecentTemplates] = useState([]);
  const [recentReviews, setRecentReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;
    const h = { Authorization: `Bearer ${token}` };

    Promise.all([
      fetch(`${API_URL}/api/creator/profile`, { headers: h }).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/creator/analytics`, { headers: h }).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/creator/reviews?limit=3`, { headers: h }).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/creator/templates?limit=4`, { headers: h }).then(r => r.ok ? r.json() : null),
    ]).then(([prof, anal, revs, tmpls]) => {
      setProfile(prof);
      setAnalytics(anal);
      setRecentReviews(revs?.items || []);
      setRecentTemplates(tmpls?.items || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleGoalSave = (cents) => {
    setProfile(p => p ? {
      ...p,
      creator_profile: {
        ...p.creator_profile,
        goals: { ...p.creator_profile?.goals, monthly_revenue_goal_cents: cents },
      },
    } : p);
  };

  const summary = analytics?.summary || {};
  const user = (() => { try { return JSON.parse(localStorage.getItem(USER_KEY) || '{}'); } catch { return {}; } })();
  const displayName = profile?.display_name || user?.name || 'Creator';

  if (loading) return (
    <CreatorLayout>
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </CreatorLayout>
  );

  return (
    <CreatorLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-2">
            Welcome back, {displayName} 👋
            <VerifiedBadge verified={profile?.verification?.status === 'verified'} />
          </h1>
          <p className="text-sm text-gray-500 mt-1">Here's how your store is performing.</p>
        </div>
        <button
          onClick={() => navigate('/creator/create')}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white text-sm font-semibold hover:opacity-90 transition-opacity shadow-lg shadow-violet-500/20"
        >
          <PlusCircle className="w-4 h-4" />
          New template
        </button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={DollarSign}
          label="Total earnings"
          value={`$${((summary.total_revenue_cents || 0) / 100).toFixed(2)}`}
          color="emerald"
        />
        <StatCard
          icon={TrendingUp}
          label="Total sales"
          value={summary.total_sales || 0}
          color="violet"
        />
        <StatCard
          icon={Eye}
          label="Template views"
          value={(summary.total_views || 0).toLocaleString()}
          color="sky"
        />
        <StatCard
          icon={LayoutTemplate}
          label="Templates"
          value={summary.total_templates || 0}
          sub={`${summary.overall_conversion_rate || 0}% conversion`}
          color="amber"
        />
      </div>

      {/* Goals + Recent Reviews */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-1">
          <GoalWidget profile={profile} earnedCents={summary.total_revenue_cents || 0} onSave={handleGoalSave} />
        </div>

        {/* Recent Reviews */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
            <p className="font-semibold text-gray-900">Recent Reviews</p>
            <button onClick={() => navigate('/creator/reviews')} className="text-xs text-violet-600 font-semibold flex items-center gap-1">
              See all <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {recentReviews.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-10">No reviews yet. Once buyers review your templates, they'll appear here.</p>
            ) : recentReviews.map(rev => (
              <div key={rev.id} className="px-5 py-4">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className={`w-3 h-3 ${i <= rev.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-100'}`} />
                    ))}
                  </div>
                  <span className="text-xs text-gray-400">{rev.user_name}</span>
                  {rev.template_name && <span className="text-xs text-gray-300">· {rev.template_name}</span>}
                </div>
                {rev.comment && <p className="text-sm text-gray-600 line-clamp-2">{rev.comment}</p>}
                {rev.creator_reply && (
                  <p className="text-xs text-violet-600 mt-1 italic">Your reply: {rev.creator_reply}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Templates */}
      {recentTemplates.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
            <p className="font-semibold text-gray-900">Recent Templates</p>
            <button onClick={() => navigate('/creator/templates')} className="text-xs text-violet-600 font-semibold flex items-center gap-1">
              See all <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {recentTemplates.map(t => (
              <div key={t.id} className="flex items-center gap-4 px-5 py-4">
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {t.thumbnail
                    ? <img src={t.thumbnail} alt="" className="w-full h-full object-cover" />
                    : <LayoutTemplate className="w-5 h-5 text-gray-400" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{t.name}</p>
                  <p className="text-xs text-gray-400 capitalize">{t.marketplace_status || t.status}</p>
                </div>
                <button
                  onClick={() => navigate(`/creator/templates`)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowRight className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {recentTemplates.length === 0 && (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-violet-50 flex items-center justify-center mx-auto mb-4">
            <LayoutTemplate className="w-7 h-7 text-violet-400" />
          </div>
          <p className="font-semibold text-gray-900">No templates yet</p>
          <p className="text-sm text-gray-400 mt-1">Create your first template and start earning.</p>
          <button
            onClick={() => navigate('/creator/create')}
            className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition-colors mx-auto"
          >
            <PlusCircle className="w-4 h-4" /> Create template
          </button>
        </div>
      )}
    </CreatorLayout>
  );
};

export default CreatorDashboard;
